import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useTranslation } from "@/contexts/LanguageContext";
import { MapPin, Phone, EnvelopeSimple, WhatsappLogo, InstagramLogo, FacebookLogo, TiktokLogo, YoutubeLogo, Globe, Clock } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.4 } } };

export default function ContactPage() {
  const [info, setInfo] = useState({});
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const { t, lang } = useTranslation();

  useEffect(() => { axios.get(`${API}/contact-info`).then(r => setInfo(r.data)).catch(() => {}); }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error(t("fill_all_fields")); return; }
    const waMsg = encodeURIComponent(`Hola Pro Vertical! Soy ${form.name} (${form.email}). ${form.message}`);
    window.open(`https://wa.me/${(info.whatsapp || "56912345678").replace(/\D/g, "")}?text=${waMsg}`, "_blank");
    toast.success(t("redirecting_wa"));
  };

  const socials = [
    info.whatsapp && { icon: WhatsappLogo, label: "WhatsApp", href: `https://wa.me/${info.whatsapp.replace(/\D/g, "")}` },
    info.instagram && { icon: InstagramLogo, label: info.instagram, href: `https://instagram.com/${info.instagram.replace("@", "")}` },
    info.facebook && { icon: FacebookLogo, label: info.facebook, href: `https://facebook.com/${info.facebook}` },
    info.tiktok && { icon: TiktokLogo, label: info.tiktok, href: `https://tiktok.com/${info.tiktok.replace("@", "")}` },
    info.youtube && { icon: YoutubeLogo, label: info.youtube, href: `https://youtube.com/${info.youtube}` },
    info.website && { icon: Globe, label: info.website, href: info.website.startsWith("http") ? info.website : `https://${info.website}` },
  ].filter(Boolean);

  return (
    <main className="pt-20 min-h-screen">
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-gray-400 mb-2">Pro Vertical</p>
          <h1 className="text-4xl sm:text-5xl tracking-tight leading-none font-black uppercase font-['Cabinet_Grotesk'] mb-4" data-testid="contact-page-title">{t("contact_title")}</h1>
          <p className="text-base text-gray-400 max-w-lg">{t("contact_desc")}</p>
        </div>
      </section>

      <section className="pb-24" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-8"
          >
            {/* Left: Contact Info */}
            <motion.div variants={fadeUp} className="space-y-6">
              {/* Main Contact */}
              <div className="bg-[#121212] border border-white/5 p-8">
                <h3 className="text-xl font-semibold mb-6 font-['Cabinet_Grotesk']">{t("contact_info")}</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <MapPin size={20} className="text-[#FF4D00] mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium">{info.address || "Coyhaique, Aysen"}</p>
                      <p className="text-xs text-gray-400">{info.city || "Coyhaique"}, {info.region || "Aysen, Chile"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone size={20} className="text-[#FF4D00] flex-shrink-0" />
                    <a href={`tel:${info.phone}`} className="text-sm hover:text-[#FF4D00] transition-colors">{info.phone || "+56 9 1234 5678"}</a>
                  </div>
                  <div className="flex items-center gap-3">
                    <EnvelopeSimple size={20} className="text-[#FF4D00] flex-shrink-0" />
                    <a href={`mailto:${info.email}`} className="text-sm hover:text-[#FF4D00] transition-colors">{info.email || "contacto@provertical.cl"}</a>
                  </div>
                  {info.schedule && (
                    <div className="flex items-center gap-3">
                      <Clock size={20} className="text-[#FF4D00] flex-shrink-0" />
                      <span className="text-sm text-gray-300">{info.schedule}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Social Media */}
              {socials.length > 0 && (
                <div className="bg-[#121212] border border-white/5 p-8">
                  <h3 className="text-xl font-semibold mb-6 font-['Cabinet_Grotesk']">{t("social_media")}</h3>
                  <div className="space-y-3">
                    {socials.map((s, idx) => (
                      <a key={idx} href={s.href} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-3 text-sm text-gray-300 hover:text-[#FF4D00] transition-colors" data-testid={`contact-social-${idx}`}>
                        <s.icon size={20} /> {s.label}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Extra Info */}
              {info.extra_info && (
                <div className="bg-[#121212] border border-white/5 p-8">
                  <h3 className="text-xl font-semibold mb-4 font-['Cabinet_Grotesk']">{lang === "en" ? "Additional Info" : "Informacion Adicional"}</h3>
                  <p className="text-sm text-gray-400 leading-relaxed whitespace-pre-wrap">{info.extra_info}</p>
                </div>
              )}

              {/* Embedded Map */}
              {info.map_embed_url && (
                <div className="bg-[#121212] border border-white/5 overflow-hidden">
                  <iframe src={info.map_embed_url} className="w-full h-64 border-0" allowFullScreen loading="lazy" title="Ubicacion" />
                </div>
              )}
            </motion.div>

            {/* Right: Contact Form */}
            <motion.div variants={fadeUp} className="bg-[#121212] border border-white/5 p-8 h-fit">
              <h3 className="text-xl font-semibold mb-6 font-['Cabinet_Grotesk']">{t("send_message")}</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div><Label className="text-gray-400 text-xs">{t("name_label")}</Label><Input className="bg-[#1A1A1A] border-white/10 mt-1" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} data-testid="contact-name-input" /></div>
                <div><Label className="text-gray-400 text-xs">{t("email")}</Label><Input type="email" className="bg-[#1A1A1A] border-white/10 mt-1" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} data-testid="contact-email-input" /></div>
                <div><Label className="text-gray-400 text-xs">{t("message_label")}</Label><Textarea className="bg-[#1A1A1A] border-white/10 mt-1 min-h-[120px]" value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))} placeholder={t("message_placeholder")} data-testid="contact-message-input" /></div>
                <Button type="submit" className="w-full bg-[#FF4D00] hover:bg-[#E64500] text-white font-bold h-12" data-testid="contact-submit-btn">{t("send_whatsapp")}</Button>
                <p className="text-xs text-gray-500 text-center">{t("whatsapp_note")}</p>
              </form>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
